import {Box, Grid, GridItem, IconButton, Input, Text, Slider, SliderTrack, SliderFilledTrack, SliderThumb, SliderMark, Tooltip} from "@chakra-ui/react";
import {AppContext, AppContextI} from "../../context/ContextWrapper";
import React, {ChangeEvent, FocusEvent, KeyboardEvent, useContext, useEffect, useState} from "react";
import {SoundFile, SoundFileLibraryEntry, Spot} from "../../types";
import {BiSolidArrowToTop, BiTrash} from "react-icons/bi";
import ManagedCheckBox from "../../form/ManagedCheckBox";
import HelpTooltip from "../../help/HelpTooltip";
import {useConfirmDialog} from "../../common/ConfirmDialogProvider";
import TimeCodeInput from "../../form/TimeCodeInput";

type Props = {
    spot: Spot,
    soundFile: SoundFile,
    libraryEntry?: SoundFileLibraryEntry,
    language: string,
    isDefaultLanguage?: boolean,
    defaultLanguageCode?: string,
    defaultRowUsesLanguageCode?: boolean,
    isAmbientSlot: boolean,
    onReplace?: () => void
}

const matchesSelectedLanguage = (
    languageValue: string | null | undefined,
    language: string,
    isDefaultLanguage?: boolean,
    defaultLanguageCode?: string,
    defaultRowUsesLanguageCode?: boolean
) => {
    if (isDefaultLanguage) {
        if (!(languageValue || "").trim()) {
            return true;
        }
        if (defaultRowUsesLanguageCode && defaultLanguageCode) {
            return languageValue === defaultLanguageCode;
        }
        return false;
    }
    return languageValue === language;
};

const SoundFileDetails = ({spot, soundFile, libraryEntry, language, isDefaultLanguage, defaultLanguageCode, defaultRowUsesLanguageCode, isAmbientSlot, onReplace}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const {confirm} = useConfirmDialog();
    const selection = appContext.getSelection();
    const isOnDemandMode = appContext.isOnDemand();
    const [playOnes, setPlayOnes] = useState<boolean>(soundFile?.playOnes ? soundFile.playOnes : false);
    const [delayTime, setDelayTime] = useState<number>(soundFile?.delay ? soundFile.delay : 0);
    const [fadeInTime, setFadeInTime] = useState<number>(soundFile?.fadeIn ? soundFile.fadeIn : 0);
    const [fadeOutTime, setFadeOutTime] = useState<number>(soundFile?.fadeOut ? soundFile.fadeOut : 0);
    const [synchronize, setSynchronize] = useState<boolean>(soundFile?.synchronize !== null ? soundFile?.synchronize : false);
    const [noSyncTime, setNoSyncTime] = useState<number>(soundFile?.noSyncTime ? soundFile.noSyncTime : 0);
    const [volumeDb, setVolumeDb] = useState<number>(soundFile?.volumeDb ?? 0);
    const [pendingVolumeDb, setPendingVolumeDb] = useState<number>(soundFile?.volumeDb ?? 0);


    useEffect(() => {
        if (selection &&
            (
                playOnes !== soundFile.playOnes ||
                fadeInTime !== soundFile.fadeIn ||
                fadeOutTime !== soundFile.fadeOut ||
                delayTime !== soundFile.delay ||
                synchronize !== soundFile.synchronize ||
                noSyncTime !== soundFile.noSyncTime ||
                volumeDb !== soundFile.volumeDb
            )
        ) {
            const newSoundFiles: SoundFile[] = [...spot.soundFiles ? spot.soundFiles : []];
            const thisFileIndex: number = newSoundFiles.findIndex((soundFl) => {
                const matchesAmbient = (soundFl.ambient ?? false) === isAmbientSlot;
                const matchesLanguage = matchesSelectedLanguage(soundFl.language, language, isDefaultLanguage, defaultLanguageCode, defaultRowUsesLanguageCode);
                return matchesAmbient && matchesLanguage;
            });

            if (thisFileIndex > -1) {
                const nextSoundFile = {
                    ...newSoundFiles[thisFileIndex],
                    ambient: isAmbientSlot,
                    playOnes: playOnes,
                    fadeIn: fadeInTime,
                    fadeOut: fadeOutTime,
                    delay: delayTime,
                    synchronize: synchronize,
                    noSyncTime: noSyncTime,
                    volumeDb: volumeDb
                };
                if (isDefaultLanguage && !(defaultRowUsesLanguageCode && defaultLanguageCode)) {
                    delete nextSoundFile.language;
                } else if (isDefaultLanguage && defaultLanguageCode) {
                    nextSoundFile.language = defaultLanguageCode;
                } else {
                    nextSoundFile.language = language;
                }
                newSoundFiles[thisFileIndex] = nextSoundFile;

                appContext.updateSpot(spot, {
                    ...spot,
                    soundFiles: newSoundFiles
                } as Spot)
            }
        }
    }, [
        appContext,
        delayTime,
        fadeInTime,
        fadeOutTime,
        defaultLanguageCode,
        defaultRowUsesLanguageCode,
        isAmbientSlot,
        isDefaultLanguage,
        language,
        noSyncTime,
        playOnes,
        selection,
        soundFile.delay,
        soundFile.fadeIn,
        soundFile.fadeOut,
        soundFile.noSyncTime,
        soundFile.playOnes,
        soundFile.synchronize,
        soundFile.volumeDb,
        spot,
        synchronize,
        volumeDb
    ])


    const parseInputValue = (value: string): number => {
        if (value.length === 0) {
            return 0;
        } else {
            const intVal = parseInt(value, 10);
            return Number.isFinite(intVal) && intVal >= 0 ? intVal : 0;
        }
    }

    const getIntValue = (event: ChangeEvent<HTMLInputElement>): number => parseInputValue(event.target.value);


    return <Box maxWidth={"full"} padding={"4px"} marginTop={"8px"}>
        <Grid gridTemplateColumns={"max-content 1fr 24px max-content max-content"} rowGap={"8px"} marginTop={"8px"}>
            <Box>Sound file:</Box>
            <Box ml={"2"}>
                {libraryEntry?.purgedAt ? (
                    <Tooltip
                        label={`Purged${libraryEntry.purgedAt ? ` on ${new Date(libraryEntry.purgedAt).toLocaleString()}` : ""}${libraryEntry.purgedBy ? ` by ${libraryEntry.purgedBy}` : ""}. Original file no longer available.`}
                        placement={"top"}
                        hasArrow
                    >
                        <Text as={"strong"} color={"red.500"}>{soundFile?.originalFileName}</Text>
                    </Tooltip>
                ) : (
                    <strong>{soundFile?.originalFileName}</strong>
                )}
            </Box>
            <Box/>
            <IconButton
                variant={"ghost"}
                aria-label={"Replace file"}
                title="Replace file"
                icon={<BiSolidArrowToTop/>}
                onClick={onReplace}
                disabled={!appContext.isDraft() || !onReplace}
            />
            <IconButton variant={"ghost"} aria-label={"Delete"} title="Delete file" icon={<BiTrash/>} onClick={async () => {
                if (!selection || !spot) {
                    return;
                }
                const approved = await confirm({
                    title: "Remove sound file",
                    body: "Are you sure you want to remove this sound file from the spot?",
                    confirmText: "Remove",
                    isDestructive: true
                });
                if (!approved) {
                    return;
                }
                const soundFiles = [...spot.soundFiles ? spot.soundFiles : []]
                const newSoundFiles = soundFiles.filter((sound) => {
                    const matchesAmbient = (sound.ambient ?? false) === isAmbientSlot;
                    const matchesLanguage = matchesSelectedLanguage(sound.language, language, isDefaultLanguage, defaultLanguageCode, defaultRowUsesLanguageCode);
                    return !(matchesAmbient && matchesLanguage);
                });

                appContext.updateSpot(selection, {
                    ...selection,
                    soundFiles: newSoundFiles
                } as Spot);
            }}
                        disabled={!appContext.isDraft()}
            />
        </Grid>
        <Grid gridTemplateColumns={"max-content 1fr"} gridColumnGap={"8px"} gridRowGap={"8px"}>
            {!isOnDemandMode ? (
                <ManagedCheckBox name={"playOnes"}
                                 defaultValue={playOnes}
                                 onChange={setPlayOnes}
                                 disabled={!appContext.isDraft()}
                >
                    Play only ones <Text fontWeight={"light"} display={"inline-block"}>(even if the spot is looping)</Text>
                </ManagedCheckBox>
            ) : null}
            {isAmbientSlot ? (
                <ManagedCheckBox
                    key={`keepPlayingAfterMainFile-${spot.id}-${language}-${soundFile.keepPlayingAfterMainFile ? "1" : "0"}`}
                    name={"keepPlayingAfterMainFile"}
                    defaultValue={soundFile.keepPlayingAfterMainFile ?? false}
                    onChange={(checked) => {
                        const soundFiles = [...(spot.soundFiles || [])];
                        const thisFileIndex = soundFiles.findIndex((soundFl) => {
                            const matchesAmbient = (soundFl.ambient ?? false) === isAmbientSlot;
                            const matchesLanguage = matchesSelectedLanguage(soundFl.language, language, isDefaultLanguage, defaultLanguageCode, defaultRowUsesLanguageCode);
                            return matchesAmbient && matchesLanguage;
                        });
                        if (thisFileIndex < 0) {
                            return;
                        }
                        soundFiles[thisFileIndex] = {
                            ...soundFiles[thisFileIndex],
                            ambient: isAmbientSlot,
                            keepPlayingAfterMainFile: checked
                        };
                        if (isDefaultLanguage && !(defaultRowUsesLanguageCode && defaultLanguageCode)) {
                            delete soundFiles[thisFileIndex].language;
                        } else if (isDefaultLanguage && defaultLanguageCode) {
                            soundFiles[thisFileIndex].language = defaultLanguageCode;
                        } else {
                            soundFiles[thisFileIndex].language = language;
                        }
                        appContext.updateSpot(spot, {
                            ...spot,
                            soundFiles: soundFiles
                        } as Spot);
                    }}
                    disabled={!appContext.isDraft()}
                >
                    Keep playing after main file
                </ManagedCheckBox>
            ) : null}
            {!isOnDemandMode ? (
                <>
                    <Text fontWeight={"semibold"}>Delay</Text>
                    <Box display={"flex"} alignItems={"center"} gap={2}>
                        <TimeCodeInput
                            valueMs={delayTime}
                            onChangeMs={setDelayTime}
                            isDisabled={!appContext.isDraft()}
                        />
                        <Text fontSize={"xs"} color={"gray.500"}>mm:ss.t</Text>
                    </Box>
                </>
            ) : null}

            <Text fontWeight={"semibold"}>Fade in</Text>
            <Box display={"flex"} alignItems={"center"} gap={2}>
                <TimeCodeInput
                    valueMs={fadeInTime}
                    onChangeMs={setFadeInTime}
                    isDisabled={!appContext.isDraft()}
                />
                <Text fontSize={"xs"} color={"gray.500"}>mm:ss.t</Text>
            </Box>

            <Text fontWeight={"semibold"}>Fade out</Text>
            <Box display={"flex"} alignItems={"center"} gap={2}>
                <TimeCodeInput
                    valueMs={fadeOutTime}
                    onChangeMs={setFadeOutTime}
                    isDisabled={!appContext.isDraft()}
                />
                <Text fontSize={"xs"} color={"gray.500"}>mm:ss.t</Text>
            </Box>


            <GridItem marginBottom={"24px"} colSpan={2}>
                <Grid gridTemplateColumns={"max-content 1fr"}>
                    <Box>
                            <ManagedCheckBox name={"synchronize"}
                                         defaultValue={synchronize}
                                         onChange={setSynchronize}
                                         disabled={!appContext.isDraft()}
                        >
                            Synchronize sound

                            <HelpTooltip content={"Group synchronization requires the USB sync dongle to be attached to the device running the app. Without it, sounds play independently for each in the group."}
                                         placement={"right"}
                                         icon
                                         iconAriaLabel={"Synchronization requirements"}/>
                        </ManagedCheckBox>
                    </Box>
                    <Box></Box>
            <GridItem colStart={2} color={synchronize ? "" : "#aaaaaa"}>
                after playing
                <Input disabled={!synchronize || !appContext.isDraft()}
                       size={"sm"} width={"60px"} marginLeft={"8px"} marginRight={"8px"}
                       onFocus={(event: FocusEvent<HTMLInputElement>) => {
                           event.target.select()
                       }}
                       defaultValue={"" + noSyncTime}
                       onBlur={(event: ChangeEvent<HTMLInputElement>) => setNoSyncTime(getIntValue(event))}
                       onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                           if (event.key === "Enter") {
                               setNoSyncTime(parseInputValue(event.currentTarget.value));
                               event.currentTarget.blur();
                           }
                       }}
                />
                seconds
            </GridItem>
        </Grid>
            </GridItem>

            <GridItem colSpan={2}>
                <Text fontWeight={"semibold"} mb={2}>Level (dB)</Text>
                <Box paddingInlineEnd={8} paddingBottom={4}>
                    <Slider
                        aria-label={"Level in dB"}
                        min={-24}
                        max={6}
                        step={1}
                        value={pendingVolumeDb}
                        onChange={setPendingVolumeDb}
                        onChangeEnd={(val) => setVolumeDb(val)}
                        isDisabled={!appContext.isDraft()}
                    >
                        {Array.from({length: 31}, (_, idx) => -24 + idx).map((val) => (
                            <SliderMark
                                key={`tick-${val}`}
                                value={val}
                                boxSize={1}
                                borderRadius={"full"}
                                bg={"gray.300"}
                                mt={3}
                            />
                        ))}
                        <SliderMark value={-24} mt={3} fontSize={"xs"} color={"gray.600"}>-24</SliderMark>
                        <SliderMark value={0} mt={3} fontSize={"xs"} color={"gray.600"}>0</SliderMark>
                        <SliderMark value={6} mt={3} fontSize={"xs"} color={"gray.600"}>+6</SliderMark>
                        <SliderTrack bg={"gray.200"}>
                            <SliderFilledTrack bg={"blue.500"}/>
                        </SliderTrack>
                        <SliderThumb/>
                    </Slider>
                </Box>
            </GridItem>

        </Grid>
    </Box>;
}

export default SoundFileDetails;
